import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import type { InventoryListItem, InventoryPatch } from "../api/types";
import { invDeliveryStatusLabel, isLowStock } from "../lib/inventory";

const COLUMNS = ["Item", "Building", "Quantity", "Reorder at", "Status", "Ordered"];

export function InventoryPage() {
  const queryClient = useQueryClient();
  const [building, setBuilding] = useState("all");
  // The Status filter mirrors the Status column: All active · Low — reorder ·
  // OK · Archived. "low"/"ok" are derived (stock level), so they share the
  // same fetched "active" list and are narrowed client-side.
  const [stockFilter, setStockFilter] = useState("active");
  const [q, setQ] = useState("");

  const buildingsQ = useQuery({
    queryKey: ["ref", "buildings"],
    queryFn: () => api.reference.buildings(),
  });

  const apiStatus = stockFilter === "archived" ? "archived" : "active";
  const listKey = ["inventory", { building, apiStatus }] as const;
  const listQ = useQuery({
    queryKey: listKey,
    queryFn: () => api.inventory.list({ building, status: apiStatus }),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: InventoryPatch }) =>
      api.inventory.update(id, patch),
    onMutate: async ({ id, patch }) => {
      await queryClient.cancelQueries({ queryKey: ["inventory"] });
      const prev = queryClient.getQueryData<{ items: InventoryListItem[] }>(listKey);
      if (prev) {
        queryClient.setQueryData<{ items: InventoryListItem[] }>(listKey, {
          items: prev.items.map((it) => (it.id === id ? { ...it, ...patch } : it)),
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(listKey, ctx.prev);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["inventory"] }),
  });

  const all = listQ.data?.items ?? [];
  const term = q.trim().toLowerCase();
  const rows = all.filter((it) => {
    const low = isLowStock(it.quantity, it.reorderThreshold);
    if (stockFilter === "low" && !low) return false;
    if (stockFilter === "ok" && low) return false;
    if (term && !it.name.toLowerCase().includes(term)) return false;
    return true;
  });

  return (
    <Layout title="Inventory">
      <div className="page-actions">
        <Link to="/inventory/new" className="btn btn-primary btn-xl">
          + Add an item
        </Link>
      </div>

      <div className="filters">
        <label className="field">
          <span>Search</span>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type to search..." />
        </label>
        <label className="field">
          <span>Building</span>
          <select value={building} onChange={(e) => setBuilding(e.target.value)}>
            <option value="all">All buildings</option>
            {(buildingsQ.data?.buildings ?? []).map((b) => (
              <option key={b.id} value={String(b.id)}>
                {b.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Status</span>
          <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)}>
            <option value="active">All active</option>
            <option value="low">Low — reorder</option>
            <option value="ok">OK</option>
            <option value="archived">Archived</option>
          </select>
        </label>
      </div>

      <section className="card">
        {listQ.isLoading ? (
          <p className="sub">Loading…</p>
        ) : listQ.isError ? (
          <p className="sub">Could not load inventory. Please try again.</p>
        ) : rows.length === 0 ? (
          <p className="sub">No items match these filters.</p>
        ) : (
          <div className="table-wrap stack-md">
            <table>
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((it) => (
                  <InventoryRow
                    key={it.id}
                    item={it}
                    onPatch={(patch) => updateMut.mutate({ id: it.id, patch })}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </Layout>
  );
}

function InventoryRow({
  item,
  onPatch,
}: {
  item: InventoryListItem;
  onPatch: (patch: InventoryPatch) => void;
}) {
  // Local state so typing a multi-digit quantity does not fire a save per
  // keystroke; the change commits on blur.
  const [qty, setQty] = useState(String(item.quantity));
  useEffect(() => {
    setQty(String(item.quantity));
  }, [item.quantity]);

  const low = isLowStock(item.quantity, item.reorderThreshold);

  function commitQty() {
    const n = Number(qty);
    if (qty !== "" && Number.isFinite(n) && n >= 0 && n !== item.quantity) {
      onPatch({ quantity: n });
    } else {
      setQty(String(item.quantity));
    }
  }

  return (
    <tr className={"inv-row" + (low ? " inv-row--low" : "")}>
      <td data-label="Item">
        <Link to={`/inventory/${item.id}`} className="wo-title-link">
          {item.name}
        </Link>
      </td>
      <td data-label="Building">{item.buildingName}</td>
      <td data-label="Quantity">
        <span className="qty-cell">
          <input
            type="number"
            min="0"
            step="any"
            className="inline-edit"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            onBlur={commitQty}
            aria-label="Quantity"
          />
          {item.unitOfMeasure && <span className="sub">{item.unitOfMeasure}</span>}
        </span>
      </td>
      <td data-label="Reorder at">{item.reorderThreshold ?? "—"}</td>
      <td data-label="Status">
        <InvStatusBadge item={item} />
      </td>
      <td data-label="Ordered">
        <span className="badge badge-info">{invDeliveryStatusLabel(item.deliveryStatus)}</span>
        <div className="sub">{item.supplier ?? "No supplier"}</div>
      </td>
    </tr>
  );
}

function InvStatusBadge({ item }: { item: InventoryListItem }) {
  if (item.status === "archived") return <span className="badge badge-muted">Archived</span>;
  if (item.status === "inactive") return <span className="badge badge-muted">Inactive</span>;
  if (isLowStock(item.quantity, item.reorderThreshold)) {
    return <span className="badge badge-alert">Low — reorder</span>;
  }
  return <span className="badge badge-ok">OK</span>;
}
